import { styled } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { brandclub_colors, hexToRgba } from '@stackline/ui';
import _get from 'lodash/get';
import React, { FC } from 'react';
import { AppName } from 'sl-api-connector';
import { EntityCardContainer } from 'src/components/BeaconV2Components/GenericSidebarNav/EntityCardContainer';
import EntityDetails from 'src/components/BeaconV2Components/GenericSidebarNav/EntityDetails';
import { useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { AppLinkTabs } from 'src/components/GlobalComponents/PrimaryPageNavigation/AppLinkTabs';
import AppQuickLink from 'src/components/GlobalComponents/PrimaryPageNavigation/AppQuickLink';

import styles from 'src/components/BeaconV2Components/styles/styles';
import {
  BEACON_PRO_SIDEBAR_ITEM_HEIGHT,
  BEACON_PRO_SIDEBAR_WIDTH
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { Widget } from 'src/types/application/widgetTypes';
import { getAppName, getQueryParamValue } from 'src/utils/app';
import colors from 'src/utils/colors';

const LabelContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  color: colors.blueGrey,
  transition: 'color 0.25s ease-in',
  '.side_nav_badge': {
    opacity: 0,
    transition: 'opacity 0.25s ease-in'
  },
  '&:hover': {
    color: colors.darkBlue,
    '.side_nav_badge': {
      opacity: 1
    }
  }
});

const BadgeContainer = styled('div')({
  fontSize: 12,
  height: 24,
  padding: '0 8px',
  display: 'flex',
  borderRadius: 4,
  alignItems: 'center'
});

const ProBadge = () => {
  const appName = getAppName();
  const colorSet =
    appName === AppName.Beacon
      ? {
          bg: '#F6E8F1',
          color: '#A21671'
        }
      : {
          bg: colors.lightestBlue,
          color: '#46A8F6'
        };
  return (
    <BadgeContainer
      className="side_nav_badge"
      sx={{
        backgroundColor: colorSet.bg,
        color: colorSet.color
      }}
    >
      Pro
    </BadgeContainer>
  );
};

export const SidebarLabel = ({ label, requireSubscription }: { label: string; requireSubscription: boolean }) => {
  return (
    <LabelContainer>
      {label} {requireSubscription ? <ProBadge /> : ''}
    </LabelContainer>
  );
};

export const useGenericSideBarStyles = makeStyles({
  container: {
    ...styles.pageCenterDiv,
    color: colors.darkBlue,
    paddingTop: 31,
    textAlign: 'left'
  },
  inner: {
    display: 'grid',
    gridTemplateColumns: '185px 1fr',
    gap: 50
  },
  innerWithoutSidebar: {
    display: 'grid',
    gridTemplateColumns: '1fr'
  },
  leftNavTitle: {
    fontSize: '1.6rem',
    fontWeight: 'bold'
  },
  tabs: {
    marginTop: 32,
    '& div.MuiTabs-flexContainer': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& > span': {
        height: '36px'
      }
    }
  },
  tab: {
    alignItems: 'flex-start',
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: 'Roboto',
    padding: '8px 10px 8px 19px',
    textAlign: 'left',
    textTransform: 'none',
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 'normal',
    letterSpacing: 'normal',

    '&.MuiButtonBase-root': {
      color: colors.darkBlue,
      borderLeft: `3px solid #E6E7E9`,
      width: BEACON_PRO_SIDEBAR_WIDTH,
      minHeight: BEACON_PRO_SIDEBAR_ITEM_HEIGHT,
      '&:hover': {
        backgroundColor: brandclub_colors.blueWhite,
        transition: 'background-color 0.25s ease-in',
        textDecoration: 'none'
      }
    },
    '&.MuiButtonBase-root.Mui-selected': {
      textRendering: 'geometricPrecision',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      backgroundColor: hexToRgba(colors.lightBlue, 0.2),
      borderLeft: `3px solid ${colors.darkBlue}`,
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px',
      fontWeight: 600,
      '&:hover': {
        backgroundColor: '#a5cfff33'
      }
    }
  }
});

const SidebarContainer = styled('div')({
  width: BEACON_PRO_SIDEBAR_WIDTH
});

const GenericSidebar: FC<{
  widget: Widget;
}> = ({ widget }) => {
  const classes = useGenericSideBarStyles();
  const tab = getQueryParamValue('tab');
  const subtab = getQueryParamValue('subtab');

  const { getCurrentRoutes, currentRoute } = useBeaconRoutes();

  const empty: boolean = _get(widget, ['data', 'empty'], false);

  if (empty) {
    return <SidebarContainer />;
  }

  const renderSidebar = () => {
    switch (tab) {
      case 'traffic': {
        switch (subtab) {
          case 'settings':
            return null;

          default:
            return (
              <>
                <AppLinkTabs
                  tabIndex={currentRoute.index}
                  setTabIndex={() => {}}
                  tabRoutes={getCurrentRoutes()}
                  tabsProps={{
                    classes: {
                      root: classes.tabs
                    }
                  }}
                  containerStyle={{
                    marginLeft: 0,
                    marginRight: 0
                  }}
                  tabProps={{
                    classes: {
                      root: classes.tab
                    }
                  }}
                />
                <AppQuickLink />
              </>
            );
        }
      }

      case 'advertising': {
        return (
          <>
            <AppLinkTabs
              tabIndex={currentRoute.index}
              setTabIndex={() => {}}
              tabRoutes={getCurrentRoutes()}
              tabsProps={{
                classes: {
                  root: classes.tabs
                }
              }}
              containerStyle={{
                marginLeft: 0,
                marginRight: 0
              }}
              tabProps={{
                classes: {
                  root: classes.tab
                }
              }}
            />
            <EntityDetails />
            <AppQuickLink />
          </>
        );
      }

      default:
        return (
          <>
            <AppLinkTabs
              tabIndex={currentRoute.index}
              setTabIndex={() => {}}
              tabRoutes={getCurrentRoutes()}
              tabsProps={{
                classes: {
                  root: classes.tabs
                }
              }}
              containerStyle={{
                marginLeft: 0,
                marginRight: 0
              }}
              tabProps={{
                classes: {
                  root: classes.tab
                }
              }}
            />
            <AppQuickLink />
          </>
        );
    }
  };

  return (
    <SidebarContainer>
      <EntityCardContainer />
      {/** Used for rendering a sidebar with a portal from anywhere else in the app */}
      <div id="sidebar-portal"></div>
      {renderSidebar && renderSidebar()}
    </SidebarContainer>
  );
};

/**
 * wraps Component with Sidebar
 * @param Component
 */
export const withSidebar = (Component: any, Sidebar?: any): FC => {
  const WrappedComponent = () => {
    const classes = useGenericSideBarStyles();
    return (
      <div className={classes.container}>
        <div className={Sidebar ? classes.inner : classes.innerWithoutSidebar}>
          {Sidebar && <Sidebar />}
          <div>
            <Component />
          </div>
        </div>
      </div>
    );
  };
  return WrappedComponent;
};

export default GenericSidebar;
